<template>
  <div class="mt-2">
    <div class="expense-card">
      <h4 class="expense-title">
        Prime <span style="color: #00a3d9">MF Tools</span> for DIY Research
      </h4>

      <b-row class="g-5 card-align" v-if="!isMobile">
        <b-col lg="3" md="6" v-for="(card, index) in cards" :key="index">
          <div class="card text-center shadow-sm h-100 responsive-padding">
            <img
              :src="card.img"
              :alt="card.title"
              class="d-block mx-auto mb-2 img-fluid rounded-circle"
              style="max-width: 64px; height: auto"
            />

            <h6 class="mb-2 title text-center">
              <span class="d-block">
                <template
                  v-for="(word, index) in getTitleParts(
                    card.title
                  ).firstLine.split(' ')"
                  :key="index"
                >
                  <span :class="{ 'fw-bold': word.toLowerCase() !== 'vs' }">
                    {{ word }}
                  </span>
                  <span
                    v-if="
                      index <
                      getTitleParts(card.title).firstLine.split(' ').length - 1
                    "
                    >&nbsp;</span
                  >
                </template>
              </span>
              <span class="d-block">{{
                getTitleParts(card.title).secondLine
              }}</span>
            </h6>

            <p class="small text-muted mb-3 custom-style">
              <span class="d-block">{{
                getDescParts(card.desc).firstLine
              }}</span>
              <span class="d-block">{{
                getDescParts(card.desc).secondLine
              }}</span>
            </p>
            <div class="d-flex justify-content-center mb-5">
              <b-button
                variant="primary"
                class="text-white w-75 border-0"
                style="background: #00a3d9 !important; font-size: 12px"
                :href="card.link"
                target="_blank"
              >
                {{ card.cta }}
              </b-button>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>

    <div
      class="position-relative expense-card swiper-container-wrapper d-block d-md-none v-else"
    >
      <Swiper
        :modules="[Pagination]"
        :slides-per-view="1"
        :space-between="20"
        :loop="true"
        :pagination="{ clickable: true, el: '.swiper-pagination-custom' }"
        class="mySwiper"
      >
        <SwiperSlide v-for="(card, index) in cards" :key="index">
          <div class="card text-center shadow-sm h-100 p-3">
            <img :src="card.img" :alt="card.title" class="mb-3 mx-auto" />
            <h6 class="fw-bold mb-2">{{ card.title }}</h6>
            <p class="small text-muted mb-3">{{ card.desc }}</p>
            <div class="d-flex justify-content-center mb-3">
              <b-Button
                variant="primary"
                class="text-white w-50 expense-button"
                style="background-color: #0169e9 !important"
                :href="card.link"
                target="_blank"
              >
                {{ card.cta }}
              </b-Button>
            </div>
          </div>
        </SwiperSlide>
      </Swiper>
    </div>
    
    <!-- Pagination outside the card -->
    <div class="swiper-pagination-custom d-block d-md-none"></div>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref } from 'vue'

import { Swiper, SwiperSlide } from 'swiper/vue'
import { Pagination } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/pagination'
const baseUrl = process.env.VUE_APP_BASE_URL
const cards = [
  {
    img: require('@/assets/images/1.svg'),
    alt: 'Compare',
    title: 'Fund return comparison',
    desc: 'See how funds score against their peers',
    cta: 'Compare now',
    link: `${baseUrl}/mutual-funds-rolling-returns-calculator/`,
  },
  {
    img: require('@/assets/images/2.svg'),
    alt: 'Portfolio',
    title: 'Equity Portfolio overlap',
    desc: 'See the common holdings in your equity funds',
    cta: 'Check overlap',
    link: `${baseUrl}/mutual-funds-overlap/`,
  },
  {
    img: require('@/assets/images/3.svg'),
    alt: 'Screen',
    title: 'Mutual Fund Screener',
    desc: 'Analyse & shortlist funds with key metrics',
    cta: 'Start screening',
    link: `${baseUrl}/mf-screener/`,
  },
  {
    img: require('@/assets/images/4.svg'),
    alt: 'Screen',
    title: 'Direct vs Regular Expense Ratio',
    desc: 'Check if your fund is more expensive than peers',
    cta: 'Compare costs',
    link: `${baseUrl}/mutual-fund-expense-ratios-direct-vs-regular/`,
  },
]

const getTitleParts = (title) => {
  const specialTitle = 'Direct vs Regular Expense Ratio'

  if (title === specialTitle) {
    return {
      firstLine: title.split(' ').slice(0, 3).join(' '),
      secondLine: title.split(' ').slice(3).join(' '),
    }
  }

  return {
    firstLine: title.split(' ').slice(0, 2).join(' '),
    secondLine: title.split(' ').slice(2).join(' '),
  }
}

const getDescParts = (desc) => {
  const specialDesc = 'Check if your fund is more expensive than peers'
  const words = desc.split(' ')

  if (desc === specialDesc) {
    return {
      firstLine: words.slice(0, 5).join(' '),
      secondLine: words.slice(5).join(' '),
    }
  }

  return {
    firstLine: words.slice(0, 4).join(' '),
    secondLine: words.slice(4).join(' '),
  }
}

const isMobile = ref(window.innerWidth <= 950)

const checkScreenSize = () => {
  isMobile.value = window.innerWidth <= 950
}
const swiperRef = ref(null)

onMounted(() => {
  window.addEventListener('resize', checkScreenSize)
  if (swiperRef.value && swiperRef.value.swiper) {
    swiperRef.value.swiper.navigation.init()
    swiperRef.value.swiper.navigation.update()
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkScreenSize)
})
</script>

<style scoped>
.expense-title {
  text-align: center;
  font-size: 45px;
  font-weight: 700;
  margin-bottom: 40px !important;
  color: #000;
  line-height: 51px;
  padding: 0 10px;
}

.warning-box {
  gap: 10px;
  font-size: 14px;
}

.title {
  line-height: 1.4;
}

.expense-card {
  padding: 30px;
  width: 100vw;
  margin-left: calc(-50vw + 50%);
}

.expense-button {
  background: none;
}

.card-align {
  padding-left: 10%;
  padding-right: 10%;
}

@media (max-width: 576px) {
  .warning-box {
    flex-direction: row;
    justify-content: center;
    gap: 10px;
  }

  .warning-icon {
    display: block;
    margin-right: 8px;
  }
}
.custom-style {
  font-size: 12px !important;
}
.card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
}

.custom-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  font-size: 24px;
  font-weight: bold;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  cursor: pointer;
}

.swiper-button-prev {
  left: -10px;
}

.swiper-button-next {
  right: -10px;
}

.carousel__slide {
  display: flex;
  justify-content: center;
}

@media (max-width: 1000px) and (min-width: 750px) {
  .responsive-padding {
    padding: 0.5rem !important;
  }
}

.swiper-pagination-custom {
  text-align: center;
  margin-top: -20px;
}

.swiper-pagination-custom .swiper-pagination-bullet {
  width: 8px;
  height: 8px;
  background: #ccc;
  opacity: 0.5;
  margin: 0 4px;
}

.swiper-pagination-custom .swiper-pagination-bullet-active {
  background: #00a3d9;
  opacity: 1;
}
</style>
